import { useParams, Link } from 'react-router-dom'
import NotFound from './NotFound'
import { useState, useRef } from 'react'
import { useClickOutside } from '../hooks/useClickOutside'
import { useManga, useBookmark, useChapterCompletions, useResetMangaProgress, useSimilarManga, useMeta } from '../api/useManga'
import { useAllChapters } from '../api/useChapter'
import { plural } from '../utils/plural'
import { useAuthStore } from '../store/authStore'
import { useReaderStore } from '../store/readerStore'
import ChapterList from '../components/manga/ChapterList'
import MangaCard from '../components/manga/MangaCard'
import StarRating from '../components/manga/StarRating'
import GenreBadge from '../components/manga/GenreBadge'
import CommentList from '../components/comments/CommentList'
import Modal from '../components/ui/Modal'
import { Skeleton, MangaCardSkeleton } from '../components/ui/Skeleton'
import { mediaUrl } from '../utils/media'
import { STATUS_CSS, BOOKMARK_STATUSES } from '../utils/constants'
import toast from 'react-hot-toast'

export default function Manga() {
  const { slug } = useParams()
  const isAuth = useAuthStore((s) => s.isAuth)
  const localCompleted = useReaderStore((s) => s.completed)
  const clearMangaProgress = useReaderStore((s) => s.clearMangaProgress)
  const [loginModal, setLoginModal] = useState(false)
  const [descExpanded, setDescExpanded] = useState(false)
  const [genresExpanded, setGenresExpanded] = useState(false)
  const [altTitlesOpen, setAltTitlesOpen] = useState(false)
  const { data: manga, isLoading, isError } = useManga(slug)
  const { data: similarManga = [], isLoading: loadingSimilar } = useSimilarManga(manga?.genres ?? [], slug)
  const { data: chapters, volumes, isLoading: loadingChapters } = useAllChapters(slug)
  const { data: serverCompleted = [] } = useChapterCompletions(manga?.id)
  const { mutate: resetOnServer } = useResetMangaProgress(manga?.id)
  const completedIds = new Set([...Object.keys(localCompleted), ...(isAuth ? serverCompleted.map(String) : [])])
  const { add: addBookmark, update: updateBookmark, remove: removeBookmark } = useBookmark(slug)
  const { data: meta } = useMeta()

  if (isLoading) return <MangaSkeleton />
  if (isError) return <NotFound />
  if (!manga) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 text-center">
        <p className="text-xl text-muted">Манга не найдена</p>
      </div>
    )
  }

  const sortedChapters = chapters ?? []
  const readCount = sortedChapters.filter((ch) => completedIds.has(ch.id)).length
  const firstChapter = sortedChapters[0]
  const lastReadChapter = sortedChapters.filter((ch) => !completedIds.has(ch.id))[0] || sortedChapters[sortedChapters.length - 1]
  const readChapter = readCount > 0 ? lastReadChapter : firstChapter

  const handleBookmarkStatus = (status) => {
    const mutation = manga.is_bookmarked ? updateBookmark : addBookmark
    mutation.mutate(status, { onError: () => toast.error('Ошибка') })
  }

  const handleRemoveBookmark = () => {
    removeBookmark.mutate(undefined, {
      onSuccess: () => toast.success('Удалено из библиотеки'),
      onError: () => toast.error('Ошибка'),
    })
  }

  const statusName = meta?.statuses?.find(s => s.code === manga.status)?.name
  const typeName = meta?.types?.find(t => t.code === manga.type)?.name

  const siteName = import.meta.env.VITE_SITE_NAME
  const metaDescription = manga.description
    ? manga.description.slice(0, 160)
    : `Читайте ${manga.title} онлайн на ${siteName}.`

  const canonicalUrl = `${window.location.origin}/manga/${manga.slug}`
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: manga.title,
    ...(manga.description && { description: manga.description }),
    ...(manga.cover_url && { image: manga.cover_url }),
    url: canonicalUrl,
    inLanguage: 'ru',
    publisher: { '@type': 'Organization', name: siteName },
    ...(manga.genres?.length && { genre: manga.genres.map((g) => g.name) }),
    ...(manga.rating > 0 && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: manga.rating.toFixed(1),
        bestRating: '10',
        ratingCount: manga.rating_count ?? 1,
      },
    }),
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <title>{manga.title} — читать онлайн | {siteName}</title>
      <meta name="description" content={metaDescription} />
      <meta property="og:type" content="book" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={`${manga.title} — читать онлайн | ${siteName}`} />
      <meta property="og:description" content={metaDescription} />
      {manga.cover_url && <meta property="og:image" content={manga.cover_url} />}
      <meta name="twitter:card" content={manga.cover_url ? 'summary_large_image' : 'summary'} />
      <link rel="canonical" href={canonicalUrl} />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      {/* Cover + info row */}
      <div className="sm:flex sm:gap-8 mb-4 sm:mb-10">
        <div className="float-left mr-4 mb-2 w-[130px] sm:float-none sm:shrink-0 sm:w-48 sm:self-stretch">
          <div className="rounded-xl overflow-hidden shadow-2xl bg-surface2 w-full aspect-[2/3] sm:aspect-auto sm:h-full">
            {manga.cover_url
              ? <img src={mediaUrl(manga.cover_url)} alt={manga.title} className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center text-4xl text-muted">📖</div>
            }
          </div>
        </div>

        <div className="space-y-3 sm:space-y-0 sm:flex sm:flex-col sm:gap-3 sm:flex-1 sm:min-w-0">
          <div>
            <h1 className="text-2xl sm:text-3xl leading-tight">{manga.title}</h1>
            {(manga.title_en || manga.title_original) && (
              <div className="mt-1.5">
                <button
                  onClick={() => setAltTitlesOpen((v) => !v)}
                  className="flex items-center gap-1 text-xs text-muted/60 hover:text-accent transition-colors"
                >
                  {altTitlesOpen ? '−' : '+'} другие названия
                </button>
                {altTitlesOpen && (
                  <div className="flex flex-col gap-0.5 mt-1.5 pl-3 border-l border-line">
                    {manga.title_original && (
                      <span className="text-xs text-muted">{manga.title_original}</span>
                    )}
                    {manga.title_en && (
                      <span className="text-xs text-muted">{manga.title_en}</span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted">
            {typeName && <span>{typeName}</span>}
            {statusName && (
              <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${STATUS_CSS[manga.status]}`}>
                {statusName}
              </span>
            )}
            {manga.year && <span>{manga.year}</span>}
            {sortedChapters.length > 0 && (
              <span>{sortedChapters.length} {plural(sortedChapters.length, ['глава', 'главы', 'глав'])}</span>
            )}
            {manga.age_rating && (
              <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${manga.age_rating === '18+' ? 'bg-red-400/15 text-red-400' : 'bg-surface2 text-muted'}`}>
                {manga.age_rating}
              </span>
            )}
          </div>

          {(manga.authors?.length > 0 || manga.translators?.length > 0) && (
            <div className="flex flex-col gap-1 text-sm">
              {manga.authors?.length > 0 && (
                <div className="flex gap-1.5">
                  <span className="text-muted shrink-0">Автор:</span>
                  <span className="text-fg font-medium">{manga.authors.join(', ')}</span>
                </div>
              )}
              {manga.translators?.length > 0 && (
                <div className="flex gap-1.5">
                  <span className="text-muted shrink-0">Перевод:</span>
                  <span className="text-fg font-medium">{manga.translators.join(', ')}</span>
                </div>
              )}
            </div>
          )}

          {manga.genres?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {manga.genres.map((g, idx) => {
                const key = typeof g === 'string' ? g : (g.id || g.slug)
                return (
                  <span key={key} className={idx >= 4 && !genresExpanded ? 'hidden sm:contents' : undefined}>
                    <GenreBadge genre={g} />
                  </span>
                )
              })}
              {manga.genres.length > 4 && (
                <button
                  className="sm:hidden text-xs text-muted hover:text-accent transition-colors px-1"
                  onClick={() => setGenresExpanded((v) => !v)}
                >
                  {genresExpanded ? 'Свернуть' : `+${manga.genres.length - 4} ещё`}
                </button>
              )}
            </div>
          )}

          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold text-muted">Рейтинги</span>
            {manga.shikimori_rating && (
              <div className="flex items-center gap-1.5 text-sm">
                <span className="text-muted w-20 shrink-0">Shikimori:</span>
                <span className="text-accent">★</span>
                <span className="font-bold text-fg">{Number(manga.shikimori_rating).toFixed(1)}</span>
                {manga.shikimori_rating_count && (
                  <span className="text-muted">({manga.shikimori_rating_count.toLocaleString('ru-RU')} оценок)</span>
                )}
              </div>
            )}
            <div className="flex items-center gap-1.5 text-sm">
              <span className="text-muted w-20 shrink-0">На сайте:</span>
              <span className="text-accent">★</span>
              <span className="font-bold text-fg">{manga.rating ? Number(manga.rating).toFixed(1) : '—'}</span>
              <span className="text-muted">
                {manga.rating_count
                  ? `(${manga.rating_count.toLocaleString('ru-RU')} ${plural(manga.rating_count, ['оценка', 'оценки', 'оценок'])})`
                  : 'Ещё нет оценок'}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-1.5 pt-1 border-t border-line">
            <span className="text-xs font-semibold text-muted">Ваша оценка</span>
            <StarRating mangaId={slug} />
          </div>

          {/* Buttons + progress — desktop only, on mobile shown below */}
          <div className="hidden sm:flex flex-wrap gap-2 mt-1">
            {readChapter && (
              <Link to={`/manga/${slug}/chapter/${readChapter.number}`}
                className="px-5 py-2.5 rounded-xl font-bold text-sm transition-all hover:opacity-90 bg-accent text-on-accent">
                {readCount > 0 ? 'Продолжить чтение' : 'Читать'}
              </Link>
            )}
            <BookmarkDropdown
              isBookmarked={manga.is_bookmarked}
              bookmarkStatus={manga.bookmark_status}
              onNeedAuth={() => setLoginModal(true)}
              isAuth={isAuth}
              onSelect={handleBookmarkStatus}
              onRemove={handleRemoveBookmark}
              isPending={addBookmark.isPending || updateBookmark.isPending || removeBookmark.isPending}
            />
          </div>

          {readCount > 0 && sortedChapters.length > 0 && (
            <div className="hidden sm:flex items-center gap-3">
              <p className="text-sm text-muted">Прочитано {readCount} из {sortedChapters.length} глав</p>
              <button
                onClick={() => {
                  clearMangaProgress(sortedChapters.map((ch) => ch.id))
                  if (isAuth) resetOnServer()
                }}
                className="text-xs text-muted hover:text-red-400 transition-colors"
                title="Отметить все главы как непрочитанные"
              >
                Сбросить прогресс
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile: buttons + progress below cover+info (L-shape) */}
      <div className="sm:hidden flex flex-col gap-3 mb-10">
        <div className="flex gap-2">
          {readChapter && (
            <Link to={`/manga/${slug}/chapter/${readChapter.number}`}
              className="flex-1 text-center px-4 py-2.5 rounded-xl font-bold text-sm transition-all hover:opacity-90 bg-accent text-on-accent">
              {readCount > 0 ? 'Продолжить чтение' : 'Читать'}
            </Link>
          )}
          <BookmarkDropdown
            isBookmarked={manga.is_bookmarked}
            bookmarkStatus={manga.bookmark_status}
            onNeedAuth={() => setLoginModal(true)}
            isAuth={isAuth}
            onSelect={handleBookmarkStatus}
            onRemove={handleRemoveBookmark}
            isPending={addBookmark.isPending || updateBookmark.isPending || removeBookmark.isPending}
            flex
          />
        </div>
        {readCount > 0 && sortedChapters.length > 0 && (
          <div className="flex items-center gap-3">
            <p className="text-sm text-muted">Прочитано {readCount} из {sortedChapters.length} глав</p>
            <button
              onClick={() => {
                clearMangaProgress(sortedChapters.map((ch) => ch.id))
                if (isAuth) resetOnServer()
              }}
              className="text-xs text-muted hover:text-red-400 transition-colors"
            >
              Сбросить прогресс
            </button>
          </div>
        )}
      </div>

      <div className="clear-both sm:clear-none" />

      {manga.description && (
        <div className="mb-10">
          <h2 className="text-lg font-bold mb-3">Описание</h2>
          <div className={`text-sm leading-relaxed text-fg ${!descExpanded ? 'line-clamp-4' : ''}`}>
            {manga.description}
          </div>
          {manga.description.length > 300 && (
            <button onClick={() => setDescExpanded((v) => !v)}
              className="mt-2 text-sm font-semibold text-accent transition-colors hover:opacity-80">
              {descExpanded ? 'Свернуть' : 'Читать полностью'}
            </button>
          )}
        </div>
      )}

      <div className="mb-12">
        <h2 className="text-lg font-bold mb-4">Главы</h2>
        <ChapterList chapters={sortedChapters} volumes={volumes} mangaSlug={slug} loading={loadingChapters} completedIds={completedIds} />
      </div>

      {(loadingSimilar || similarManga.length > 0) && (
        <div className="mb-12">
          <h2 className="text-lg font-bold mb-4">Похожие тайтлы</h2>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
            {loadingSimilar
              ? Array.from({ length: 6 }).map((_, i) => <MangaCardSkeleton key={i} />)
              : similarManga.map((m) => <MangaCard key={m.id} manga={m} meta={meta} />)
            }
          </div>
        </div>
      )}

      <div>
        <CommentList mangaId={slug} />
      </div>

      <Modal isOpen={loginModal} onClose={() => setLoginModal(false)} title="Войдите в аккаунт">
        <p className="text-sm mb-4 text-muted">Чтобы добавить в закладки, необходимо авторизоваться.</p>
        <div className="flex gap-2">
          <Link to="/login" onClick={() => setLoginModal(false)}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-center transition-all hover:opacity-90 bg-accent text-on-accent">
            Войти
          </Link>
          <Link to="/register" onClick={() => setLoginModal(false)}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-center border border-line text-fg transition-all hover:border-accent">
            Регистрация
          </Link>
        </div>
      </Modal>
    </div>
  )
}

function BookmarkDropdown({ isBookmarked, bookmarkStatus, isAuth, onNeedAuth, onSelect, onRemove, isPending, flex }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useClickOutside(ref, () => setOpen(false))

  const handleToggle = () => {
    if (!isAuth) { onNeedAuth(); return }
    setOpen(o => !o)
  }

  const handleSelect = (status) => {
    setOpen(false)
    onSelect(status)
  }

  const handleRemove = () => {
    setOpen(false)
    onRemove()
  }

  const currentLabel = bookmarkStatus
    ? BOOKMARK_STATUSES.find(s => s.key === bookmarkStatus)?.label
    : null

  return (
    <div ref={ref} className={`relative ${flex ? 'flex-1' : ''}`}>
      <button
        onClick={handleToggle}
        disabled={isPending}
        className={`${flex ? 'w-full' : ''} px-5 py-2.5 rounded-xl font-bold text-sm transition-all border flex items-center gap-1.5 justify-center
          ${isBookmarked
            ? 'bg-accent-dim text-accent border-accent/40'
            : 'bg-surface text-fg border-line hover:border-accent'
          }`}
      >
        {isBookmarked ? `✓ ${currentLabel ?? 'В библиотеке'}` : '+ В библиотеку'}
        <span className="text-xs opacity-60">▾</span>
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 z-50 min-w-[160px] rounded-xl bg-surface border border-line shadow-xl overflow-hidden">
          {BOOKMARK_STATUSES.map((s) => (
            <button
              key={s.key}
              onClick={() => handleSelect(s.key)}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors hover:bg-surface2
                ${bookmarkStatus === s.key ? 'text-accent font-semibold' : 'text-fg'}`}
            >
              {s.label}
            </button>
          ))}
          {isBookmarked && (
            <>
              <div className="h-px bg-line mx-3" />
              <button
                onClick={handleRemove}
                className="w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-surface2 transition-colors"
              >
                Удалить из библиотеки
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function MangaSkeleton() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex gap-6 mb-10">
        <Skeleton className="rounded-xl shrink-0 w-[130px] sm:w-48 aspect-[2/3] sm:aspect-auto sm:self-stretch" />
        <div className="flex flex-col gap-3 flex-1">
          <Skeleton className="h-8 w-3/4 rounded" />
          <Skeleton className="h-4 w-1/4 rounded" />
          <Skeleton className="h-4 w-2/3 rounded" />
          <Skeleton className="h-4 w-1/2 rounded" />
        </div>
      </div>
    </div>
  )
}
